import React, { useEffect, useState, useCallback } from 'react';
import { Phone, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { agentsAPI, phonenumbersAPI } from './api';
import { Card, Badge, PageTitle } from './ui';
import { LoadingRows, ErrorNote } from './DemoCustomerAIPages';

const DemoDeploy = () => {
  const [agents, setAgents] = useState(null);
  const [numbers, setNumbers] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [aRes, nRes] = await Promise.all([
        agentsAPI.list(),
        phonenumbersAPI.list().catch(() => ({ data: { numbers: [] } })),
      ]);
      setAgents(aRes.data.agents || []);
      setNumbers(nRes.data.numbers || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const deployable = (agents || []).filter((a) => a.scriptId);
  const agentForScript = (scriptId) =>
    (agents || []).find((a) => scriptId && String(a.scriptId) === String(scriptId)) || null;

  const assign = async (num, dir, agentId) => {
    const agent = (agents || []).find((a) => String(a._id) === String(agentId)) || null;
    const scriptId = agent?.scriptId || null;
    const field = dir === 'inbound' ? 'inboundScriptId' : 'outboundScriptId';
    setNumbers((list) => (list || []).map((n) => (n._id === num._id ? { ...n, [field]: scriptId } : n)));
    try {
      await phonenumbersAPI.update(num._id, { [field]: scriptId });
    } catch (e) {
      alert(`Failed to update: ${e.response?.data?.message || e.message}`);
      load();
    }
  };

  const selectClass =
    'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13px] outline-none focus:border-teal-500';

  return (
    <div className="flex h-full flex-col">
      <PageTitle
        title="Deploy"
        subtitle="Connect phone numbers to your agents — choose which agent answers inbound calls and which places outbound calls."
      />

      <Card className="mt-6 flex min-h-0 flex-1 flex-col overflow-hidden">
        {error && <div className="p-6"><ErrorNote error={error} /></div>}
        {!error && (agents === null || numbers === null) && (
          <div className="p-6"><LoadingRows label="Loading numbers…" /></div>
        )}
        {!error && agents !== null && numbers !== null && (
          <div className="demo-scroll min-h-0 flex-1 overflow-auto">
            <table className="w-full text-left">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="border-b border-slate-100 text-[11.5px] font-semibold uppercase tracking-wide text-slate-400">
                  <th className="px-6 py-3">Phone Number</th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5"><PhoneIncoming size={13} /> Inbound agent</span>
                  </th>
                  <th className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5"><PhoneOutgoing size={13} /> Outbound agent</span>
                  </th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {numbers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-16 text-center text-[13.5px] text-slate-400">
                      No phone numbers yet — add one under Conversational AI › Phone Numbers.
                    </td>
                  </tr>
                )}
                {numbers.map((n) => {
                  const inA = agentForScript(n.inboundScriptId);
                  const outA = agentForScript(n.outboundScriptId);
                  const live = Boolean(inA || outA);
                  return (
                    <tr key={n._id} className="hover:bg-slate-50/60">
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-3">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
                            <Phone size={18} strokeWidth={1.9} />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-[13.5px] font-semibold">{n.number}</p>
                            {n.friendlyName && <p className="truncate text-[12px] text-slate-500">{n.friendlyName}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <select value={inA?._id || ''} onChange={(e) => assign(n, 'inbound', e.target.value)} className={selectClass}>
                          <option value="">Not connected</option>
                          {deployable.map((a) => (
                            <option key={a._id} value={a._id}>{a.name || 'Untitled'}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3.5">
                        <select value={outA?._id || ''} onChange={(e) => assign(n, 'outbound', e.target.value)} className={selectClass}>
                          <option value="">Not connected</option>
                          {deployable.map((a) => (
                            <option key={a._id} value={a._id}>{a.name || 'Untitled'}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3.5">
                        <Badge tone={live ? 'green' : 'gray'}>{live ? 'Live' : 'Idle'}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {deployable.length === 0 && numbers.length > 0 && (
              <p className="px-6 py-4 text-[12.5px] text-slate-400">
                No deployable agents yet — an agent needs a linked script before it can answer a number.
              </p>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default DemoDeploy;
